// Admin-panel tabs that can be granted to a staff member. Mirrors the server's
// STAFF_GRANTABLE_TABS (server/src/utils/roles.ts). 'tenants' is intentionally
// excluded — it is admin-only (it's where staff are managed).
export const STAFF_GRANTABLE_TABS = [
   'overview',
   'devices',
   'transfers',
   'credits',
   'distributor',
   'settings',
   'firmware',
   // The shop, split by job. Packing and approving money are different levels
   // of trust; the shop's own settings stay admin-only because that is where
   // the deposit rule and the account numbers live.
   'shop-orders',
   'shop-payments',
   'shop-products',
   // Writing only — help guides, news, blog and the setup guides. Split out of
   // 'settings' so a writer no longer needs the system settings, the master
   // vouchers and the rewards catalog just to publish a guide.
   'posts',
] as const;

export type StaffTab = (typeof STAFF_GRANTABLE_TABS)[number];

/**
 * The tabs that live INSIDE the Piso WiFi admin panel.
 *
 * Not the same as the full grantable list any more: Shop and Posts are their
 * own screens at their own addresses. Keep this list as the one answer to "does
 * this person have any reason to open the Piso WiFi panel?" — the profile menu
 * and the panel's own tab strip both read it, so they cannot drift apart and
 * offer someone a panel with nothing in it.
 */
export const WIFI_PANEL_TABS = [
   'overview',
   'devices',
   'transfers',
   'credits',
   'distributor',
   'settings',
   'firmware',
] as const satisfies readonly StaffTab[];

/** Does this permission list justify opening the Piso WiFi panel at all? */
export const canOpenWifiPanel = (permissions: readonly string[]): boolean =>
   (WIFI_PANEL_TABS as readonly string[]).some((t) => permissions.includes(t));

// Human labels + a one-line hint of what each tab can do, shown in the
// add-to-staff permissions checklist.
export const STAFF_TAB_META: Record<StaffTab, { label: string; hint: string }> = {
   overview: { label: 'Overview', hint: 'Fleet stats and revenue charts' },
   devices: { label: 'Devices', hint: 'Device list, sessions, config & OTA rollout' },
   transfers: { label: 'Transfers', hint: 'Device transfer & sale records' },
   credits: { label: 'Credits', hint: 'Credit-package purchase approvals' },
   distributor: { label: 'Distributor', hint: 'Distributor applications & partners' },
   settings: { label: 'Settings', hint: 'System settings & rewards catalog' },
   firmware: { label: 'Firmware', hint: 'Firmware registry: upload, activate, delete' },
   'shop-orders': {
      label: 'Shop — packing',
      hint: 'Open orders, print slips, mark packed and shipped. Cannot approve money',
   },
   'shop-payments': {
      label: 'Shop — payments',
      hint: 'Approve, reject and refund the money a buyer sent',
   },
   'shop-products': {
      label: 'Shop — products',
      hint: 'Add and edit products, prices, stock and pictures',
   },
   posts: {
      label: 'Posts',
      hint: 'Write help guides, news, blog & setup guides',
   },
};

// Sections of the Devices tab that can be granted one by one. Mirrors the
// server's STAFF_DEVICE_SECTIONS (server/src/utils/roles.ts). Granted as
// `devices:<section>` strings inside the same staffPermissions array.
//
// Back-compat rule (same as the server): the plain `devices` grant with NO
// section keys means "all sections", so staff added before this existed keep
// the whole tab.
export const STAFF_DEVICE_SECTIONS = [
   'stats',
   'coins',
   'firmware',
   'fleet',
   'setup',
   'sessions',
] as const;

export type StaffDeviceSection = (typeof STAFF_DEVICE_SECTIONS)[number];

export const STAFF_DEVICE_SECTION_META: Record<
   StaffDeviceSection,
   { label: string; hint: string }
> = {
   stats: { label: 'Stat cards', hint: 'Fleet revenue, device counts, revenue today' },
   coins: { label: 'Coins today', hint: 'Live coin ticker & revenue chart' },
   firmware: { label: 'Firmwares', hint: 'Device type, setup script & router firmware rollout' },
   fleet: { label: 'Fleet device status', hint: 'The main device list & filters' },
   setup: { label: 'Setup in progress', hint: 'Devices still being provisioned' },
   sessions: { label: 'Active sessions', hint: 'Currently connected customers' },
};

/** `devices:stats` → `stats`; anything else → null. */
export const parseDeviceSection = (perm: string): StaffDeviceSection | null => {
   const [tab, section] = perm.split(':');
   if (tab !== 'devices' || !section) return null;
   return (STAFF_DEVICE_SECTIONS as readonly string[]).includes(section)
      ? (section as StaffDeviceSection)
      : null;
};

/** A grantable permission string: a tab, or a `devices:<section>` key. */
export const isStaffPermission = (perm: string): boolean =>
   (STAFF_GRANTABLE_TABS as readonly string[]).includes(perm) ||
   parseDeviceSection(perm) !== null;

/**
 * Which Devices-tab sections a permission list allows.
 * No `devices` grant → none. `devices` with no section keys → all.
 */
export const resolveDeviceSections = (
   permissions: readonly string[]
): StaffDeviceSection[] => {
   if (!permissions.includes('devices')) return [];
   const picked = permissions
      .map(parseDeviceSection)
      .filter((s): s is StaffDeviceSection => s !== null);
   return picked.length
      ? [...STAFF_DEVICE_SECTIONS].filter((s) => picked.includes(s))
      : [...STAFF_DEVICE_SECTIONS];
};
